<template>
  <div class="article-page" :class="{ 'full-width': fullWidth }">
    <div v-if="previousArticle || nextArticle" class="prev-next-links above">
      <div>
        <a
          v-if="previousArticle"
          @click.prevent="onClickPrevNext(previousArticle)"
          :href="'/article/' + previousArticle.slug"
          >« {{ previousArticle.title }}</a
        >
      </div>
      <div>
        <a
          v-if="nextArticle"
          @click.prevent="onClickPrevNext(nextArticle)"
          :href="'/article/' + nextArticle.slug"
          >{{ nextArticle.title }} »</a
        >
      </div>
    </div>

    <div class="article-title-wrapper">
      <h1 id="article-title-text">{{ article.title }}</h1>
      <button
        class="btn-floating transparent z-depth-0 create-button"
        v-if="!previewArticle && isAdmin"
        @click="editArticle"
      >
        <i class="material-icons">create</i>
      </button>
      <button
        class="btn-floating transparent z-depth-0 favorites-button"
        @click="addFavorite"
      >
        <i class="material-icons star">
          {{ isFavorite ? "star" : "star_outline" }}
        </i>
      </button>
      <button
        class="btn-floating transparent z-depth-0 unfold-button"
        @click="fullWidth = !fullWidth"
        v-if="showUnfoldButton"
      >
        <i class="material-icons">{{
          fullWidth ? "unfold_less" : "unfold_more"
        }}</i>
      </button>
    </div>

    <div v-html="markdown" id="markdown-text"></div>

    <div
      v-if="previousArticle || nextArticle"
      class="prev-next-links below mt-5"
    >
      <div>
        <a
          v-if="previousArticle"
          @click.prevent="onClickPrevNext(previousArticle)"
          :href="'/article/' + previousArticle.slug"
          >« {{ previousArticle.title }}</a
        >
      </div>
      <div>
        <a
          v-if="nextArticle"
          @click.prevent="onClickPrevNext(nextArticle)"
          :href="'/article/' + nextArticle.slug"
          >{{ nextArticle.title }} »</a
        >
      </div>
    </div>
  </div>
</template>

<script>
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import marked from "marked";
import $ from "jquery";
import { Rest } from "@/utils";
import { worker } from "@/utils";
import { lineNumbers } from "@/services/AddLineNumbers";

@Component
export default class ArticlePage extends Vue {
  @Prop() previewArticle;
  eventListeners = [];
  codeLineWidth = 0;
  fullWidth = false;
  showUnfoldButton = true;

  article = {
    title: "",
    content: "",
  };
  previousArticle = false;
  nextArticle = false;
  codeElements = [];

  get markdown() {
    return marked(this.article.content);
  }

  get user() {
    return this.$store.state.user.loggedInUser;
  }

  get isAdmin() {
    return this.$store.getters.isAdmin;
  }

  get isFavorite() {
    return !!this.$store.state.user.favorites.filter(
      (a) => a.id === this.article.id
    )[0];
  }

  get isMobile() {
    return this.$store.state.isMobile;
  }

  getSiblingArticle(dir) {
    const allArticles = this.$store.state.article.articles;
    const index = allArticles.findIndex((art) => art.id == this.article.id);
    if (index <= 0 && dir < 0) {
      return false;
    } else if (index == allArticles.length && dir > 0) {
      return false;
    }
    return allArticles[index + dir];
  }

  editArticle() {
    this.$router.push({
      name: "createArticle",
      params: { slug: this.article.slug },
    });
  }

  addFavorite() {
    if (this.isFavorite) {
      this.$store.commit("removeFavorite", this.article);
    } else {
      this.$store.commit("addFavorite", this.article);
    }
  }

  onClickPrevNext(article) {
    this.$router.push({
      name: "article",
      params: { slug: article.slug },
    });
  }

  created() {
    this.onCreated();
  }

  @Watch("$route.params.slug")
  async onCreated() {
    if (this.previewArticle) {
      this.article = this.previewArticle;
      this.postProcessContent();
      return;
    }

    const dbArticle = await Rest.get("Article", {
      properties: { slug: this.$route.params.slug },
    });

    this.article = dbArticle[0];
    this.postProcessContent();

    this.previousArticle = this.getSiblingArticle(-1);
    this.nextArticle = this.getSiblingArticle(1);

    // add to last visited
    this.$store.commit("addVisited", this.article);
  }

  highlightCode() {
    $(".code-block").each((i, el) => {
      let code = $(el);
      let w = new worker((e) => {
        self.importScripts(location.origin + "/uploads/highlight.min.js");
        self.postMessage(self.hljs.highlightAuto(e.data).value);
        self.close();
      });
      w.postMessage(code.text().replace(/^\d+/gm, ""));

      w.onmessage = (e) => {
        code.html(lineNumbers(e.data, null, true));
        this.addIndentation(code);
      };
    });
  }

  addIndentation(el) {
    let newHtml = $(el)
      .html()
      .replace(
        /(\d*__indentpx__)/g,
        (x) => (x.split("_")[0] / 1) * this.codeLineWidth
      );
    $(el).html(newHtml);
  }

  fixIndentations() {
    // fix indentation of code (and add the actual code)

    // only do once
    if (this.doneFixCodeListings) return;
    this.doneFixCodeListings = true;

    // the width of one space
    let el, firstCode;
    el = $('<span style="display:inline-block">X</span>');
    firstCode = $("code").eq(0);
    firstCode.append(el);
    this.codeLineWidth = el.width();
    el.remove();

    $(".code-block").each((i, el) => {
      this.addIndentation(el);
      // Materialize icons: content_copy, assignment
      let btn = $(`
        <button type="button" id="copy-button" class="v-btn v-btn--icon v-btn--small theme--light">
          <div class="v-btn__content">
            <i aria-hidden="true" class="v-icon material-icons theme--light" style="font-size: 20px;">content_copy</i>
          </div>
        </button>
      `);

      // btn.hide();

      // btn.mouseenter(() => {
      //   clearTimeout(this.copyBtnHover);
      // });

      // $(el).hover(
      //   () => {
      //     btn.fadeIn(150);
      //   },
      //   () => {
      //     this.copyBtnHover = setTimeout(() => {
      //       btn.fadeOut(100);
      //     }, 50);
      //   }
      // );

      this.eventListeners.push(btn);
      this.eventListeners.push($(el));

      $(el)
        .parent()
        .prepend(btn);
      btn.click(() => {
        let copyWithoutLineNumbers = $(el).clone();
        copyWithoutLineNumbers.find(".line-number").remove();
        let ta = $("<textarea/>");
        ta.text(copyWithoutLineNumbers.text());
        $("body").append(ta);
        ta.get(0).select();
        document.execCommand("copy");
        ta.remove();
      });
    });
  }

  postProcessContent() {
    this.makeATagsBlank();
    this.initCode();
  }

  makeATagsBlank() {
    setTimeout(() => {
      $("#markdown-text a:not([target])")
        .toArray()
        .filter((a) => a.getAttribute("href") && 
          (!a.getAttribute("href").startsWith("/") || !a.getAttribute("href").startsWith("#"))) // Exclude local links
        .forEach((a) => a.setAttribute("target", "_blank"));

      // remove target _blank <a> tags wrapping images
      let img = $('a > div > img').parent()
      img.parent().parent().html(img)
    }, 0);
  }

  initCode() {
    setTimeout(
      () => {
        // first load indentation is wrong when fixing
        ArticlePage.hasLoaded = true;
        this.fixIndentations();
        this.highlightCode();
      },
      ArticlePage.hasLoaded ? 0 : 300
    );
  }

  onContentResize() {
    if(this.isMobile) {
      this.showUnfoldButton = false
      return
    } 

    let mainContent = document.querySelector("#main-content");
    if (mainContent.clientWidth && mainContent.clientWidth <= 800) {
      this.showUnfoldButton = false;
    } else this.showUnfoldButton = true;
  }

  mounted() {
    let elems = document.querySelectorAll('.materialboxed');
    this.$M.Materialbox.init(elems);

    window.addEventListener("resize", this.onContentResize);

    if(this.$route.name !== 'createArticle') {
      document.querySelector('#content').style.setProperty('grid-template-columns', '230px 1fr 0')
    }

    setTimeout(() => {
      this.onContentResize();
    }, 0);
  }

  beforeDestroy() {
    for (let item of this.eventListeners) {
      $(item).unbind("mouseenter");
      $(item).unbind("mouseleave");
    }
    window.removeEventListener("resize", this.onContentResize);
  }
}
</script>

<style lang="scss">
#content > #main-content > .article-page.main-content-anim {
  transition-duration: 300ms;
  width: 740px;
}

.mobile #content > #main-content > .article-page.main-content-anim {
  max-width: 740px;
  width: auto;
}

#content > #main-content > .article-page.main-content-anim.full-width {
  width: 100%;
}

.article-page {
  height: 100%;
  text-align: start;
  position: relative;
  padding: 0 15px 30px 12px;
  font-family: "Libre Franklin", "Helvetica Neue", helvetica, arial, sans-serif;
  .article-title-wrapper {
    display: grid;
    grid-template-columns: auto 40px 40px 40px;
    align-items: baseline;
    padding: 0;
    .btn-floating {
      i {
        font-size: 2em !important;
      }
    }

    #article-title-text {
      grid-column: 1/2;
    }
    .create-button {
      grid-column: 2/3;
      i:hover {
        color: grey !important;
      }
    }
    .favorites-button {
      grid-column: 3/4;
      i:hover {
        color: grey !important;
      }
    }
    .unfold-button {
      grid-column: 4/5;
      transform: rotate(90deg);
      i:hover {
        color: grey !important;
      }
    }
  }

  @media (max-width: 600px) {
    padding-top: 0;

    .article-title-wrapper {
      grid-template-columns: repeat(12, 1fr) !important;
      #article-title-text {
        grid-column: 1/13;
        grid-row: 2;
        padding-top: 0.25em !important;
      }
      .create-button {
        grid-column: 1/2;
        grid-row: 1;
      }
      .favorites-button {
        grid-column: 2/3;
        grid-row: 1;
      }
    }
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0 0 0.3em !important;
    padding: 0.8em 0 0 !important;
  }

  #article-title-text {
    font-size: 1.875em !important;
    padding-bottom: 1em !important;
  }

  h1 {
    font-weight: 300 !important;
  }

  .prev-next-links {
    display: flex;
    justify-content: space-between;

    &.above {
      border-bottom: solid 1px darkgray;
    }
    &.below {
      border-top: solid 1px darkgray;
    }
  }
}
#markdown-text {
  font-size: 1.125em;
  line-height: 1.66;
  word-wrap: anywhere;
  a {
    font-style: italic;
    color: #000000;
    border-bottom: 1px solid black;
    &:hover {
      transition: 0.2s;
      border-bottom: 3px solid black;
    }
  }

  h1 {
    font-size: 1.875em;
  }

  h2 {
    font-size: 1.625em;
    font-weight: 800 !important;
  }
  h3 {
    font-size: 1.375em;
    font-weight: 300 !important;
  }
  h4 {
    color: rgb(71, 71, 71);
    font-size: 1.125em;
    letter-spacing: 0.06em;
    font-weight: 800;
  }
  h5 {
    color: grey;
    font-size: 0.8125em;
    font-weight: 800;
    letter-spacing: 0.13em;
    line-height: 1.5;
    text-transform: uppercase;
  }
  h6 {
    font-size: 1em;
    font-weight: 800;
  }
  .btn-small {
    transition-duration: 100ms;
    color: whitesmoke;
    border-radius: 4px;
    background-color: #333;
    &:hover {
      background-color: #aaa;
    }
  }
  p,
  li {
    font-family: "Lora", serif;
  }
  p {
    margin-bottom: 0.75em;
  }
  ul,
  ol {
    margin-bottom: 16px;
  }
  ul {
    list-style-type: revert;
    li {
      list-style-type: inherit;
    }
  }
  img {
    max-width: 100%;
    height: auto;
  }
  table {
    border-collapse: collapse;
    width: 100%;
  }
  th:first-child,
  td:first-child {
    padding-left: 0;
  }
  thead th {
    border-bottom: 2px solid #bbb;
    padding-bottom: 0.5em;
  }
  tbody {
    tr {
      border-bottom: 1px solid rgb(224, 224, 224);
      td {
        padding: 0.4em;
      }
    }
  }
  #copy-button {
    position: absolute;
    z-index: 1;
    top: 10px;
    right: 10px;
    opacity: 0.7;
    &:hover {
      opacity: 1;
    }
  }
  pre {
    white-space: pre-wrap;
    word-break: keep-all;
    margin: 1.3em 0.5em 2em !important;
    position: relative;
    background-color: var(--main-background-color) !important;
    -webkit-filter: brightness(97%);
    filter: brightness(97%);
    line-height: 1.2em;
    padding: 0.4em 0;

    @media screen and(max-width: 600px) {
      margin: 0 -10px;
    }
  }
  code {
    font-family: "Inconsolata", monospace;
    width: 100%;
    color: var(--main-text-color);
    font-weight: 500;
    position: relative;
    border-radius: 2px;
    box-shadow: none;
    padding: 0.5em 0.5em 0 3.5em;
    word-wrap: anywhere;
    overflow-x: auto;
    .hljs-ln-line {
      display: inline-block;
      margin-left: 3.55em;
      span {
        font-weight: 600;
      }
      &.first-hljs-line, & > .hljs-ln-line {
        margin-left: 0;
      }
    }
    &::before,
    &::after {
      display: none;
    }
  }

  .line-number {
    display: inline-block;
    position: absolute;
    left: 0;
    color: #ccc;
    padding: 0 0.5em;
    width: 3em;
    text-align: right;
    line-height: 1.6em;

    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .line-border {
    height: calc(100% - 10px);
    position: absolute;
    top: 9px;
    left: 3em;
    border-right: 1px solid #ccc;
  }

  .inline-code {
    padding: 0;
    display: inline;
  }
}
</style>
